import axiosInstance from './axiosInstance';
import axiosInstanceAI from './axiosInstanceAI';
import axiosInstanceNotoken from './axiosInstanceNotoken';
import {
  SignUpDto,
  UpdateUserDto,
  CreateKidDto,
  UpdateKidDto,
  CreateFreeboardPostDto,
  CreateComplaintDto,
  AiJob,
} from '../../types';

export const authApi = {
  mokStdRequest: () => {
    return axiosInstance.post('/mok/mok_std_request', null, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: '*/*',
      },
    });
  },
  mokStdResult: (data: any) => {
    return axiosInstance.post('/mok/mok_std_result', data, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: '*/*',
      },
    });
  },
  login: (data: SignUpDto) => {
    return axiosInstance.post('/user/login', data);
  },
  kakaoLogin: (data: any) => {
    return axiosInstance.post('/user/login/kakao/native', data);
  },
  updateToken: (data: { refreshToken: string }) => {
    return axiosInstance.post('/api/user/refresh-token', data);
  },
  setToken: (token: string) => {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  },
  clearToken: () => {
    axiosInstance.defaults.headers.common.Authorization = '';
  },
  getToken: () => {
    return axiosInstance.defaults.headers.common.Authorization;
  },
};

export const userApi = {
  getMyInfo: () => {
    return axiosInstance.get('/user/profile');
  },
  registerKid: (data: CreateKidDto) => {
    return axiosInstance.post('/kids', data);
  },
  editMyProfileImage: (userId: number, fd: FormData) => {
    return axiosInstance.post(`/user/profile-image/${userId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  editMyInfo: (data: UpdateUserDto) => {
    return axiosInstance.patch('/user/user', data);
  },
  signout: () => {
    return axiosInstance.delete('/user/user');
  },
  hardDelete: () => {
    return axiosInstance.delete('/user/user/hard');
  },
};

export const kidsApi = {
  registerKid: (data: CreateKidDto) => {
    return axiosInstance.post('/kids', data);
  },
  removeKid: (kidId: number) => {
    return axiosInstance.delete(`/kids/${kidId}`);
  },
  editKid: (kidId: number, data: UpdateKidDto) => {
    return axiosInstance.patch(`/kids/${kidId}`, data);
  },
  editKidProfileImage: (kidId: number, fd: FormData) => {
    return axiosInstance.post(`/kids/${kidId}/profile-image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getKids: (userId: number) => {
    const query = new URLSearchParams({ userId: userId.toString() });
    return axiosInstance.get(`/kids?${query}`);
  },
  getKidDetail: (kidId: number) => {
    return axiosInstance.get(`/kids/${kidId}`);
  },
  getKidReportDetail: (reportId: number) => {
    return axiosInstance.get(`report/${reportId}/full-analysis`);
  },
  // 문답영상 상세조회
  getQuestionVideoDetail: (mediaId: number) => {
    return axiosInstance.get(`/report/${mediaId}/intelligences`);
  },
};

export const reportApi = {
  postThumbnailVideoSet: (fd: FormData) => {
    return axiosInstance.post('/report/analyze', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  postThumbnailVideoSet2: (fd: FormData, option: any) => {
    return axiosInstance.post('/report/analyze', fd, option);
  },
  getReportsCompareData: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`report/intelligence-compare?${query}`);
  },
  getReportsTotalCompareData: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/report/kid-intelligence-compare?${query}`);
  },
  getAllVideos: (userId: number) => {
    return axiosInstance.get(`/report/recent/${userId}`);
  },
  getVideosByMonth: (userId: number) => {
    return axiosInstance.get(`/report/monthly/${userId}`);
  },
  getVideosByIntelligence: (userId: number, intelligence: string) => {
    return axiosInstance.get(`/report/intelligence/${userId}/${intelligence}`);
  },
  makeFavoriteVideo: (mediaId: number, favorite: boolean) => {
    const query = new URLSearchParams({ favorite: favorite.toString() });
    return axiosInstance.patch(`/report/${mediaId}/favorite?${query}`);
  },
  getFavoriteVideos: (userId: number) => {
    return axiosInstance.get(`/report/favorites/${userId}`);
  },
  getIntelligenceCoreByVideo: (mediaId: number) => {
    return axiosInstance.get(`/report/${mediaId}/extremes`);
  },
  removeReportVideo: (url: string) => {
    return axiosInstance.post(`/report/delete-media`, { url });
  },
  removeReport: (reportId: number) => {
    return axiosInstance.delete(`/report/${reportId}`);
  },
  makeReportSuccess: (reportId: number) => {
    return axiosInstance.patch(`/report/${reportId}/success`);
  },
  getAiJobStatus: (jobId: number) => {
    return axiosInstance.get<AiJob>(`/ai-jobs/${jobId}`);
  },
  getActiveAiJobs: () => {
    return axiosInstance.get('/ai-jobs/me/active');
  },
};

export const aiApi = {
  getKidVideoThumbnail: (fd: FormData) => {
    return axiosInstanceAI.post(`/thumbnail`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  extractThumbnails: (data: { videoFile: any }) => {
    const fd = new FormData();
    fd.append('video', data.videoFile);
    return axiosInstanceAI.post(`/thumbnail`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  analysisReq: async (
    params: {
      kidId: number | string;
      mi: any[];
      videoFile: any;
      mimeType: string;
      type: number;
      reportId?: number | string;
      impression?: string;
      physical?: string;
      behavior?: string;
      isOnlyOne: boolean;
      controller: AbortController;
    },
    setAnalysisResult: (res: any) => void
  ) => {
    try {
      const fd = new FormData();
      fd.append('video', params.videoFile);
      fd.append('kidId', String(params.kidId));
      fd.append('mi', JSON.stringify(params.mi));
      fd.append('type', String(params.type));
      if (params.reportId) fd.append('reportId', String(params.reportId));
      if (params.impression) fd.append('impression', params.impression);
      if (params.physical) fd.append('physical', params.physical);
      if (params.behavior) fd.append('behavior', params.behavior);
      fd.append('isOnlyOne', String(params.isOnlyOne));

      const res = await axiosInstanceAI.post('/analyze', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: params.controller.signal,
      });

      if (res.status === 200 || res.status === 201) {
        setAnalysisResult({ status: 'success', data: res.data, error: null, info: null });
      }
    } catch (e: any) {
      if (e.name === 'CanceledError' || e.message === 'canceled') return;
      setAnalysisResult({ status: 'error', data: null, error: e, info: null });
    }
  },
};

export const communityApi = {
  // 공통
  getComplaintReasons: () => {
    return axiosInstance.get(`/complaints/reasons`);
  },
  postComplaint: (data: CreateComplaintDto) => {
    return axiosInstance.post(`/complaints`, data);
  },
  // 공지사항
  getNoticeList: () => {
    return axiosInstanceNotoken.get(`/notices`);
  },
  getNoticeListSearch: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstanceNotoken.get(`/notices/search?${query}`);
  },
  // 이벤트
  getEventList: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstanceNotoken.get(`/events?${query}`);
  },
  getEventDetail: (eventId: number) => {
    return axiosInstanceNotoken.get(`/events/${eventId}`);
  },
  createEventComment: (eventId: number, data: { content: string }) => {
    return axiosInstance.post(`/events/${eventId}/comments`, data);
  },
  deleteEventComment: (commentId: number) => {
    return axiosInstance.delete(`/events/comments/${commentId}`);
  },
  getEventComments: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/events/comments/pagination?${query}`);
  },
  // Q&A
  createQna: (data: any) => {
    return axiosInstance.post(`/questions`, data);
  },
  editQna: (postId: number, data: any) => {
    return axiosInstance.patch(`/questions/${postId}`, data);
  },
  getQnaList: () => {
    return axiosInstanceNotoken.get(`/questions`);
  },
  getQnaListSearch: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstanceNotoken.get(`/questions/search?${query}`);
  },
  getQnaDetail: (postId: number, userId: number) => {
    const query = new URLSearchParams({ userId: userId.toString() });
    return axiosInstanceNotoken.get(`/questions/${postId}?${query}`);
  },
  getMyQnaDetail: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/questions/search/user?${query}`);
  },
  createComment: (postId: number, data: { content: string }) => {
    return axiosInstance.post(`/questions/${postId}/answers`, data);
  },
  toggleLike: (postId: number) => {
    return axiosInstance.patch(`/questions/${postId}/toggle-like`);
  },
  deletePost: (postId: number) => {
    return axiosInstance.delete(`/questions/${postId}`);
  },
  deleteComment: (commentId: number) => {
    return axiosInstance.delete(`/questions/answers/${commentId}`);
  },
  getQnaComments: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/questions/comment/pagination?${query}`);
  },
  getExpertAnswer: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/questions/comment/expert?${query}`);
  },
  // 자유게시판
  createFreePost: (data: CreateFreeboardPostDto) => {
    return axiosInstance.post(`/freeboard`, data);
  },
  getFreePostList: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstanceNotoken.get(`/freeboard?${query}`);
  },
  getFreePostDetail: (userId: number, postId: number) => {
    const query = new URLSearchParams({ userId: userId.toString() });
    return axiosInstanceNotoken.get(`/freeboard/${postId}?${query}`);
  },
  createFreePostComment: (postId: number, data: { content: string }) => {
    return axiosInstance.post(`/freeboard/${postId}/comments`, data);
  },
  toggleFeePostLike: (postId: number) => {
    return axiosInstance.post(`/freeboard/${postId}/likes/toggle`);
  },
  deleteFreeComment: (commentId: number) => {
    return axiosInstance.delete(`/freeboard/comments/${commentId}`);
  },
  getFreeComments: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/freeboard/comment/pagination?${query}`);
  },
};

export const myApi = {
  // 자주묻는질문
  getFrequentQnaList: () => {
    return axiosInstance.get('/faq');
  },
  getFrequentQnaCategory: () => {
    return axiosInstance.get(`/faq/categories`);
  },
  getFrequentQnaDetail: (qnaId: number) => {
    return axiosInstance.get(`/faq/${qnaId}`);
  },
  // 1:1 문의
  postPrivateQna: (data: any) => {
    return axiosInstance.post('/qna', data);
  },
  getPrivateQnaList: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/qna/my?${query}`);
  },
  getPrivateQnaDetail: (qnaId: number) => {
    return axiosInstance.get(`/qna/${qnaId}`);
  },
  deletePrivateQna: (qnaId: number) => {
    return axiosInstance.delete(`/qna/${qnaId}`);
  },
  // 내 자유게시판
  getMyFreeboardList: (queryValue: any) => {
    const query = new URLSearchParams(queryValue);
    return axiosInstance.get(`/freeboard/search/user?${query}`);
  },
  deleteMyFreeboard: (freeboardId: number) => {
    return axiosInstance.delete(`/freeboard/${freeboardId}`);
  },
  getMyFreeboardDetail: (freeboardId: number) => {
    return axiosInstance.get(`/freeboard/${freeboardId}`);
  },
};
